import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ComplianceBanner from "@/components/ComplianceBanner";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/products", { params: { category: category || undefined, q: params.get("q") || undefined } })
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, [category, params]);

  const activeCat = useMemo(() => categories.find((c) => c.slug === category), [categories, category]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (q) p.set("q", q); else p.delete("q");
    setParams(p);
  };

  return (
    <div className="page-enter">
      <ComplianceBanner />
      <section className="border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 pb-10">
          <div className="overline mb-4">Catalog</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight">
            {activeCat ? activeCat.name : "All research products"}
          </h1>
          {activeCat?.description && <p className="mt-4 text-zinc-600 max-w-2xl">{activeCat.description}</p>}
        </div>
      </section>

      <section className="border-b border-white/10 bg-white/[0.02] backdrop-blur">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-4 flex flex-wrap gap-2 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategory("")}
              className={`px-3 py-1.5 border rounded-full text-sm transition-colors ${!category ? "bg-blue-500 text-white border-blue-500" : "border-white/15 text-zinc-300 hover:border-white/40 hover:text-white"}`}
              data-testid="cat-all"
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(c.slug)}
                className={`px-3 py-1.5 border rounded-full text-sm transition-colors ${category === c.slug ? "bg-blue-500 text-white border-blue-500" : "border-white/15 text-zinc-300 hover:border-white/40 hover:text-white"}`}
                data-testid={`cat-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <form onSubmit={submitSearch} className="flex items-center border border-white/15 rounded-full overflow-hidden focus-within:border-blue-500">
            <MagnifyingGlass size={16} className="ml-3 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, CAS, slug"
              className="px-3 py-2 outline-none text-sm w-64 bg-transparent"
              data-testid="shop-search"
            />
            {q && (
              <button type="button" onClick={() => { setQ(""); const p = new URLSearchParams(params); p.delete("q"); setParams(p); }} className="p-1 mr-1" aria-label="Clear">
                <X size={14} />
              </button>
            )}
            <button type="submit" className="btn-prm !py-2 !px-4" data-testid="shop-search-submit">Search</button>
          </form>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          {products.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-xl p-16 text-center">
              <div className="overline mb-2">No results</div>
              <div className="text-zinc-400">Try clearing filters or the search term.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
